import React, { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ChevronLeft,
  ChevronRight,
  Eye,
  FolderOpen,
  Settings2,
  SlidersHorizontal,
} from 'lucide-react'
import MessengerLayout from '../../../layout/MessengerLayout'
import '../../../components/cases/cases.css'
import { orgCases, caseTypes } from '../../../components/admin/cases/adminApi'
import { NOT_ENTITLED } from '../../../components/cases/api'
import { Button, Chip, Empty, Notice, Panel, Refusal, Spinner } from '../../../components/cases/ui'
import { date, pluralise, words } from '../../../components/cases/format'

// ======================================================================
// THE ORGANISATION CASE LIST
// ======================================================================
//
// The one screen in this module that shows an administrator cases they
// hold no party row on, and it is deliberately the thinnest possible
// version of that: a reference, a type, a stage, a status, two dates,
// the policy clock and the owner's name. No title, no subject, no
// reporter, no allegation, no evidence, no finding. The backend does
// not send them — schemas.OrgCaseListItem is `extra="forbid"` with
// eleven fields — so there is nothing here that could accidentally
// render one.
//
// ======================================================================
// WHY THE ROWS DO NOT CLICK, AND HOW THAT IS MADE LEGIBLE
// ======================================================================
//
// There is no case id on the response. Not omitted from the render —
// ABSENT from the payload, because returning one would hand an admin a
// working probe against GET /api/cases/{case_id}, which they cannot
// open, and every refusal writes a DURABLE access_denied row into the
// audit chain of a case they cannot read. A fifty-row list fetched per
// row would stamp fifty denials into fifty chains on every page load,
// and the case owner reading that trail afterwards would see what looks
// exactly like somebody probing their case.
//
// So a row cannot navigate. Three things stop that reading as broken,
// and the third is the one that matters:
//
//   1. NOTHING LOOKS CLICKABLE. No <Link>, no chevron, no hover lift,
//      no pointer cursor. The row is a table row, and a table row is
//      not a promise. Compare pages/cases/MyCases.jsx, where every row
//      IS a link and carries a ChevronRight — the two screens are
//      visibly different kinds of thing, on purpose.
//
//   2. THE RULE IS STATED ONCE, AT THE TOP, IN PLAIN WORDS, before the
//      user has had a chance to try. Not a tooltip on a failed click,
//      and not an error afterwards.
//
//   3. THE ROW ENDS IN THE NEXT ACTION RATHER THAN A DEAD END. The last
//      column is the case owner, labelled so it reads as "the person to
//      ask". An admin looking at a stalled case does not actually want
//      to read it — they want to know who to chase — and that is
//      exactly what the row gives them. A screen that answers the real
//      question does not feel like one that is withholding something.
//
// ======================================================================
// THERE IS NO SEARCH BOX, AND THERE IS NO CLIENT-SIDE FILTER EITHER
// ======================================================================
//
// The backend deliberately has none: over a list you cannot open,
// search is only useful for finding out whether a named person has a
// case. Filtering the loaded page in the browser has the same effect at
// a smaller scope and would be the same feature with the audit row
// already written. The filters below are properties of a CASELOAD —
// type, status, breach — and every one of them is applied by the
// server.
//
// ======================================================================
// THE CLOCK IS READ, NEVER COMPUTED
// ======================================================================
//
// `breached` and `in_reminder_window` are fields. Nothing here does
// `new Date() > target_close_at`: that is a different answer wearing
// the same colour — wrong across a weekend, wrong on a paused case
// whose clock is frozen at paused_at, and wrong by an entire reminder
// window whose length only the tenant's configuration knows. The same
// rule pages/cases/MyCases.jsx follows, from the same fields, computed
// by the same acl._clock_flags on the server.

const PAGE_SIZE = 50
/** The backend's cap is 200 (er_cases/admin_routes.MAX_PAGE_SIZE) and
 *  asking for more is a 422 before the handler runs. 50 is the default
 *  page; the size selector below offers 100 and 200 and stops there
 *  rather than letting a request be refused. */
const PAGE_SIZES = [50, 100, 200]

const SORTS = [
  { value: 'target', label: 'Target — soonest first' },
  { value: '-target', label: 'Target — latest first' },
  { value: '-opened', label: 'Opened — newest first' },
  { value: 'opened', label: 'Opened — oldest first' },
]

const STATUSES = [
  { value: '', label: 'Any status' },
  { value: 'open', label: 'Open' },
  { value: 'paused', label: 'Paused' },
  { value: 'closed', label: 'Closed' },
]

const BREACH = [
  { value: '', label: 'Any' },
  { value: 'true', label: 'Past target' },
  { value: 'false', label: 'Within target' },
]

export default function OrgCaseList() {
  const [filters, setFilters] = useState({
    caseType: '',
    status: '',
    breached: '',
    sort: 'target',
    limit: PAGE_SIZE,
    offset: 0,
  })

  const [page, setPage] = useState(null)
  const [types, setTypes] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // The type filter needs the tenant's case types, and that list is on
  // the OTHER admin surface — which is not behind step-up and writes no
  // audit row. One fetch, on mount, never refetched: procedures change a
  // handful of times a year.
  useEffect(() => {
    let cancelled = false
    caseTypes()
      .then((res) => {
        if (cancelled || res === NOT_ENTITLED || !Array.isArray(res)) return
        setTypes(res)
      })
      // A failure here loses the type filter and nothing else. The list
      // itself is unaffected, so this is deliberately not surfaced as an
      // error banner over a working screen.
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])

  const load = useCallback(() => {
    setLoading(true)
    setError(null)
    return orgCases({
      caseType: filters.caseType || undefined,
      status: filters.status || undefined,
      breached: filters.breached === '' ? undefined : filters.breached === 'true',
      sort: filters.sort,
      limit: filters.limit,
      offset: filters.offset,
    })
      .then((res) => {
        if (res === NOT_ENTITLED) {
          setPage({ total: 0, limit: filters.limit, offset: 0, cases: [] })
          return
        }
        setPage(res)
      })
      .catch(setError)
      .finally(() => setLoading(false))
  }, [filters])

  useEffect(() => {
    load()
  }, [load])

  const set = (patch) =>
    // ANY FILTER CHANGE RETURNS TO PAGE ONE. Keeping the offset across a
    // filter change lands the user on an empty page and reads as "there
    // are none", which is a different and wrong answer.
    setFilters((f) => ({ ...f, ...patch, offset: 'offset' in patch ? patch.offset : 0 }))

  const rows = page?.cases || []
  const total = page?.total ?? 0
  const showing = rows.length
  const filtered = filters.breached !== ''

  return (
    <MessengerLayout>
      <div className="cases-root min-h-screen">
        {/* FULL WIDTH (task 26). This screen benefits most: it is a
            seven-column table of a whole organisation's caseload, and
            every pixel of margin was a column squeezed. */}
        <div className="w-full px-5 py-6 sm:px-8">
          <Header />

          <div className="mt-5">
            <Notice tone="accent" icon={Eye} title="What this list is">
              Every case in your organisation, so the process can be overseen —
              how long each one has been running, whether it is late, and who is
              responsible for it. It deliberately carries no case content: no
              subject, no allegations, no evidence, no notes.{' '}
              <strong className="font-semibold text-ink">
                Opening a case requires being added to it as a party
              </strong>
              , so the rows here do not open. The last column is the person to
              ask.
            </Notice>
          </div>

          <div className="mt-4">
            <Filters
              filters={filters}
              set={set}
              types={types}
              disabled={loading && !page}
            />
          </div>

          {error && (
            <div className="mt-4">
              <Refusal error={error} onDismiss={() => setError(null)} />
            </div>
          )}

          <div className="mt-4">
            <Panel
              eyebrow="Organisation caseload"
              title={
                loading && !page
                  ? 'Loading'
                  : filtered
                    ? `${pluralise(showing, 'case', 'cases')} shown`
                    : `${pluralise(total, 'case', 'cases')}`
              }
              right={
                <Pager
                  page={page}
                  filters={filters}
                  set={set}
                  busy={loading}
                  filtered={filtered}
                />
              }
            >
              {loading && !page ? (
                <Spinner label="Reading the organisation's caseload" />
              ) : rows.length === 0 ? (
                <EmptyState filters={filters} />
              ) : (
                <>
                  <CaseTable rows={rows} />
                  {filtered && (
                    // THE BREACH FILTER IS APPLIED AFTER THE PAGE, on the
                    // server, in Python — `breached` is a function of the
                    // tenant's working-day calendar and of every pause a
                    // case has been through, so it is not a column and
                    // cannot be a WHERE clause. The consequence is that a
                    // filtered page can hold fewer rows than the page
                    // size. Said out loud rather than left to look like
                    // a bug.
                    <p className="mt-3 text-[11.5px] leading-relaxed text-faint">
                      Showing {showing} of the {total} cases on this page&rsquo;s
                      other filters. Whether a case is past its target depends on
                      your organisation&rsquo;s working-day calendar and on any
                      time it spent paused, so it is worked out per case rather
                      than by the database — which is why a filtered page can be
                      shorter than a full one.
                    </p>
                  )}
                </>
              )}
            </Panel>
          </div>
        </div>
      </div>
    </MessengerLayout>
  )
}

function Header() {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4">
      <div>
        <div className="eyebrow">Case administration</div>
        <h1 className="mt-1 font-head text-[22px] font-bold leading-tight text-ink">
          Organisation cases
        </h1>
      </div>
      <Link to="/admin/cases/types" className="btn">
        <Settings2 size={14} strokeWidth={1.9} />
        Case types
      </Link>
    </div>
  )
}

function Filters({ filters, set, types, disabled }) {
  return (
    <section className="panel px-5 py-4">
      <div className="mb-3 flex items-center gap-2">
        <SlidersHorizontal size={14} strokeWidth={1.9} className="text-faint" />
        <span className="eyebrow">Filter</span>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Select
          label="Case type"
          value={filters.caseType}
          onChange={(v) => set({ caseType: v })}
          disabled={disabled}
          options={[
            { value: '', label: 'All case types' },
            ...types.map((t) => ({ value: t.key, label: t.label })),
          ]}
        />
        <Select
          label="Status"
          value={filters.status}
          onChange={(v) => set({ status: v })}
          disabled={disabled}
          options={STATUSES}
        />
        <Select
          label="Target"
          value={filters.breached}
          onChange={(v) => set({ breached: v })}
          disabled={disabled}
          options={BREACH}
        />
        <Select
          label="Sort by"
          value={filters.sort}
          onChange={(v) => set({ sort: v })}
          disabled={disabled}
          options={SORTS}
        />
      </div>
      {/* NO SEARCH BOX. See the block at the top of this file. The
          absence is deliberate and is the kind of thing a later reader
          would otherwise "fix". */}
    </section>
  )
}

function Select({ label, value, onChange, options, disabled }) {
  return (
    <label className="block">
      <span className="mb-1.5 block font-head text-[12px] font-semibold text-ink">
        {label}
      </span>
      <select
        className="field"
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  )
}

function Pager({ page, filters, set, busy, filtered }) {
  const total = page?.total ?? 0
  const offset = filters.offset
  const limit = filters.limit
  const from = total === 0 ? 0 : offset + 1
  const to = Math.min(offset + limit, total)
  const canPrev = offset > 0
  const canNext = offset + limit < total

  return (
    <div className="flex items-center gap-2">
      <select
        className="field w-auto py-1.5 text-[12px]"
        value={limit}
        disabled={busy}
        onChange={(e) => set({ limit: Number(e.target.value) })}
        aria-label="Cases per page"
      >
        {PAGE_SIZES.map((n) => (
          <option key={n} value={n}>
            {n} per page
          </option>
        ))}
      </select>
      <span className="mono whitespace-nowrap text-faint">
        {from}–{to} of {total}
        {filtered ? ' matched' : ''}
      </span>
      <Button
        size="sm"
        variant="ghost"
        disabled={!canPrev || busy}
        onClick={() => set({ offset: Math.max(0, offset - limit) })}
        aria-label="Previous page"
      >
        <ChevronLeft size={14} strokeWidth={1.9} />
      </Button>
      <Button
        size="sm"
        variant="ghost"
        disabled={!canNext || busy}
        onClick={() => set({ offset: offset + limit })}
        aria-label="Next page"
      >
        <ChevronRight size={14} strokeWidth={1.9} />
      </Button>
    </div>
  )
}

/** THE ROWS.
 *
 *  A real <table>, not a list of cards: this is tabular data an
 *  administrator scans down a column of, and a table gives the column
 *  headers a screen reader can announce per cell. It is also, plainly,
 *  not a list of links.
 *
 *  Row tone follows the policy clock, exactly as MyCases does — and the
 *  flag is repeated IN WORDS as a chip, because a coloured left border
 *  is invisible to a screen reader and to anybody who does not know the
 *  convention, and "this case is late" is not something to encode in a
 *  hue on a disciplinary caseload. */
function CaseTable({ rows }) {
  return (
    <div className="-mx-1 overflow-x-auto">
      <table className="w-full min-w-[860px] border-collapse text-left">
        <thead>
          <tr className="border-b border-line">
            {['Reference', 'Case type', 'Stage', 'Status', 'Opened', 'Target', 'Case owner'].map(
              (h) => (
                <th
                  key={h}
                  scope="col"
                  className="eyebrow px-2 pb-2 pt-1 font-semibold"
                >
                  {h}
                </th>
              ),
            )}
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => (
            <CaseRow key={c.reference} c={c} />
          ))}
        </tbody>
      </table>
    </div>
  )
}

function CaseRow({ c }) {
  // breached wins over in_reminder_window. They are mutually exclusive
  // server-side — timeline_position sets the window flag only when NOT
  // breached — but the order is written down rather than assumed,
  // because a client that quietly depended on that would be wrong the
  // day it stopped being true.
  const tone = c.breached
    ? 'border-l-2 border-l-[var(--danger)]'
    : c.in_reminder_window
      ? 'border-l-2 border-l-[var(--amber)]'
      : 'border-l-2 border-l-transparent'

  return (
    // A ROW HIGHLIGHT ON HOVER, ADDED WITH THE FULL-WIDTH CHANGE (task
    // 26). A seven-column table spanning a 2560px window puts the
    // reference and the case owner a long way apart, and the eye loses
    // the row on the way across. The highlight is the cheapest fix and
    // the honest one — it does NOT make the row clickable (there is no
    // case id and nothing to navigate to; see the block at the top of
    // this file), so the cursor stays default and nothing lifts.
    <tr
      className={`border-b border-line/60 align-top transition-colors hover:bg-surface-2 ${tone}`}
    >
      <td className="px-2 py-3">
        <span className="mono text-accent">{c.reference}</span>
      </td>
      <td className="px-2 py-3 text-[12.5px] text-muted">{c.case_type_label}</td>
      <td className="px-2 py-3">
        {/* THE LABEL COMES FROM THE VERSION THE CASE PINS. It is null
            when the case sits on a stage its own version no longer
            names — a real, reachable state after a case type edit — and
            the key is shown instead of a fabricated label. */}
        <span className="text-[12.5px] text-muted">
          {c.current_stage_label || words(c.current_stage)}
        </span>
        {!c.current_stage_label && (
          <span
            className="ml-1.5 text-[11px] text-faint"
            title="This case is on a stage its own version of the procedure no longer lists."
          >
            (retired stage)
          </span>
        )}
      </td>
      <td className="px-2 py-3">
        <div className="flex flex-wrap items-center gap-1.5">
          <Chip tone={c.status === 'closed' ? undefined : 'accent'}>
            {words(c.status)}
          </Chip>
          {c.breached && <Chip tone="danger">Past target</Chip>}
          {!c.breached && c.in_reminder_window && (
            <Chip tone="amber">Target approaching</Chip>
          )}
        </div>
      </td>
      <td className="px-2 py-3 text-[12.5px] text-muted">{date(c.opened_at)}</td>
      <td className="px-2 py-3 text-[12.5px]">
        {c.target_close_at ? (
          <span className={c.breached ? 'text-danger' : 'text-muted'}>
            {date(c.target_close_at)}
          </span>
        ) : (
          <span className="text-faint">—</span>
        )}
      </td>
      <td className="px-2 py-3 text-[12.5px]">
        {c.case_owner ? (
          <span className="text-ink">{c.case_owner}</span>
        ) : (
          // A CASE WITH NO OWNER IS THE ONE AN ADMIN MOST NEEDS TO SEE,
          // so the gap is stated rather than left blank. It happens
          // while an owner is being replaced.
          <span className="text-amber" title="No active case owner on this case.">
            Unassigned
          </span>
        )}
      </td>
    </tr>
  )
}

/** THE EMPTY STATE, AND IT SAYS WHICH KIND OF EMPTY IT IS.
 *
 *  "No cases in this organisation" and "no cases matching these
 *  filters" are different facts, and an administrator who has just
 *  filtered needs to be told which one they are looking at — otherwise
 *  the honest answer "there are none" reads as a broken screen. */
function EmptyState({ filters }) {
  const narrowed =
    filters.caseType !== '' || filters.status !== '' || filters.breached !== ''

  if (narrowed) {
    return (
      <Empty icon={FolderOpen} title="No cases match these filters">
        Nothing in your organisation matches the case type, status and target
        you have chosen. Widen the filters to see the rest of the caseload.
      </Empty>
    )
  }

  return (
    <Empty icon={FolderOpen} title="No cases have been opened yet">
      Nothing has been raised in your organisation. Cases are opened by a
      manager, an admin or an executive from the Cases module, and they will
      appear here as soon as they are — this screen needs no setting up.
    </Empty>
  )
}
